import { RefObject, useEffect } from "react";

declare global {
    interface Window {
        externalWindow: Window | null;
    }
}

export const useOutsideClick = (
    refs: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[],
    callback: () => void,
    isExternal = false,
) => {
    const handleClick = (e: any) => {
        let theseRefs = refs;
        if (!Array.isArray(refs)) theseRefs = [refs];

        // The event path as it was when the click was dispatched, which is the only reliable record
        // of where the click came from. `contains(e.target)` alone is not: a control that unmounts
        // as a result of its own click — a confirmation card that disappears once you answer it —
        // is already detached by the time the event bubbles up here, so its own container no longer
        // "contains" it and an inside click reads as an outside one, closing the popper.
        const path: EventTarget[] = typeof e.composedPath === "function" ? e.composedPath() : [];

        let contains = false;
        //@ts-ignore
        theseRefs.forEach((ref) => {
            if (ref.current && (ref.current.contains(e.target) || path.includes(ref.current))) {
                contains = true;
            }
        });

        if (!contains) callback();
    };

    // TODO see if we can check if is in external window without passing in isExternal
    // const checkIfExternal = () => {
    //     if (window.externalWindow && ref.current) {
    //         console.log(ref.current.closest("#new-window-container"));
    //     }
    // };

    const handleWindowBlur = () => {
        // When an iframe receives a click, the parent window loses focus
        // and document.activeElement becomes the iframe element.
        // Use setTimeout so the browser updates activeElement before we check.
        setTimeout(() => {
            const active = document.activeElement;
            if (active?.tagName === "IFRAME") {
                let theseRefs = refs;
                if (!Array.isArray(refs)) theseRefs = [refs];

                // A popper rendered inside the focused iframe (the Miles snippet) was clicked, not
                // left. Cross-origin frames throw or return null here and still count as outside.
                let frameDoc: Document | null = null;
                try {
                    frameDoc = (active as HTMLIFrameElement).contentDocument;
                } catch {
                    frameDoc = null;
                }

                let contains = false;
                (theseRefs as RefObject<HTMLElement | null>[]).forEach((ref) => {
                    if (
                        ref.current &&
                        (ref.current.contains(active) || ref.current.ownerDocument === frameDoc)
                    ) {
                        contains = true;
                    }
                });

                if (!contains) callback();
            }
        }, 0);
    };

    useEffect(() => {
        // checkIfExternal();
        if (isExternal && window.externalWindow) {
            window.externalWindow.document.addEventListener("click", handleClick);
        } else {
            document.addEventListener("click", handleClick);
        }

        window.addEventListener("blur", handleWindowBlur);

        return () => {
            if (isExternal && window.externalWindow) {
                window.externalWindow.document.removeEventListener("click", handleClick);
            } else {
                document.removeEventListener("click", handleClick);
            }

            window.removeEventListener("blur", handleWindowBlur);
        };
    });
};
